import { window } from "global";
import { useEffect } from "react";
import { MediaQuery } from "@outplayed/responsive";
import { TierListHeader } from "@ugg/shared/components/TierList/TierListHeader";
import { TierListPageNav } from "@ugg/shared/components/TierList/TierListPageNav";
import { TierListFilterManager } from "@ugg/shared/components/TierList/TierListFilterManager";
import { TierListTablePlaceholder } from "@ugg/shared/components/TierList/common/TierListTablePlaceholder";
import { TierListTableError } from "@ugg/shared/components/TierList/common/TierListTableError";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { SPECIAL_TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { ChampionLink } from "@ugg/shared/components/TierList/common/ChampionLink";
import { QueueTypeC, QueueTypeN } from "@ugg/shared/utils/queue-type-helpers";
import { useChampionProfileContext } from "@ugg/shared/components/Champions/ChampionProfileContext";
import {
  AugmentRarity,
  AugmentManifest,
  useAramMayhemChampionTierList,
  useAramMayhemAugmentTierList,
  useAramMayhemAugmentManifest,
  useAramMayhemAugmentByRarityTierList,
  useAramMayhemPerChampionAugmentTierList,
} from "@ugg/shared/hooks/aram-mayhem";

import {
  ALL_TIERS,
  TierNameValue,
  ARAM_MAYHEM_TIER_LABEL_BG,
  ARAM_MAYHEM_TIER_BANNER_CLASSES,
  ARAM_MAYHEM_TIER_BODY_BG,
} from "@ugg/shared/utils/tier-helpers";

type BannerProp = {
  bannerSlot?: JSX.Element | null;
  patchNotesCTA?: React.ReactNode;
};

// Iteration order for the by-rarity augment table — Prismatic → Gold
// → Silver, matching the canonical RARITY_DISPLAY_ORDER on the Python
// side (best-to-worst). Explicit constant (rather than Object.keys()
// over the fetched data) so the React order stays stable even if
// upstream JSON key ordering ever changes.
const RARITY_ORDER = ["kPrismatic", "kGold", "kSilver"] as const satisfies readonly AugmentRarity[];

// Human-readable rarity labels for section headers. Decoupled from
// the JSON's `k*` enum keys so localization / display tweaks live
// here instead of in every render site.
const RARITY_DISPLAY: Record<AugmentRarity, string> = {
  kPrismatic: "Prismatic",
  kGold: "Gold",
  kSilver: "Silver",
};

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

interface AramMayhemChampionTierListTableProps {
  tierlist: Record<TierNameValue, number[]>;
}

export function AramMayhemChampionTierListTable(props: AramMayhemChampionTierListTableProps) {
  const { tierlist } = props || {};
  const convertedQueueType = QueueTypeC.convertToString(QueueTypeN.ARAM_MAYHEM) as string;

  return (
    <div className="flex flex-col gap-6">
      {ALL_TIERS.map((tier) => {
        const champions = tierlist[tier];
        if (!champions || champions.length === 0) return null;
        return (
          <section key={tier} aria-label={`Tier ${tier}`} className="overflow-hidden rounded-[3px]">
            <div
              className={`flex h-14 items-center gap-3 border-l-4 bg-[#191937] bg-gradient-to-r to-transparent px-[18px] ${ARAM_MAYHEM_TIER_BANNER_CLASSES[tier]}`}
            >
              <span className={`block h-5 w-0.5 rounded-sm ${ARAM_MAYHEM_TIER_LABEL_BG[tier]}`} aria-hidden="true" />
              <h2 className="text-sm font-bold text-white">{tier}</h2>
            </div>
            <div className={`h-px ${ARAM_MAYHEM_TIER_LABEL_BG[tier]}`} aria-hidden="true" />
            <div
              className={`grid grid-cols-1 gap-x-[18px] gap-y-[18px] border-l-4 p-[18px] sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 ${ARAM_MAYHEM_TIER_BODY_BG[tier]} ${ARAM_MAYHEM_TIER_BANNER_CLASSES[tier]}`}
            >
              {champions.map((championId) => (
                <ChampionLink
                  key={championId}
                  championId={championId}
                  queueType={convertedQueueType}
                  dim={36}
                  className="[&_.champion-name]:!text-sm [&_.champion-name]:!ml-0 [&>div]:!px-0 !gap-[8px]"
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Augment tier rows — private inner helper shared by both augment
// tables. Renders one set of tier rows from a {tier: [augment_id]}
// map. Lifted out so AramMayhemAugmentTierListTable (one set of rows,
// flat) and AramMayhemAugmentByRarityTierListTable (one set per
// rarity, three sections) don't duplicate the same render logic.
//
// Visual layout mirrors the champion table — same tier-row card,
// same colored left bar, same responsive grid — so the augment and
// champion sections feel visually consistent when stacked on the
// same page. The per-cell content is a placeholder until the augment
// asset pipeline lands: a dashed-border square stands in for the
// future icon, with the augment ID rendered as text beside it.
// Swap the inner card contents (icon img, localized name, hover
// behavior) without touching this surrounding structure.
// ─────────────────────────────────────────────────────────────────────

interface AugmentTierRowsProps {
  tierlist: Record<TierNameValue, number[]>;
  manifest: AugmentManifest;
  patch: string;
}

function AugmentTierRows(props: AugmentTierRowsProps) {
  const { tierlist, manifest, patch } = props || {};

  return (
    <div className="flex flex-col gap-6">
      {ALL_TIERS.map((tier) => {
        const augmentIds = tierlist[tier];
        if (!augmentIds || augmentIds.length === 0) return null;
        return (
          <section key={tier} aria-label={`Tier ${tier}`} className="overflow-hidden rounded-[3px]">
            <div
              className={`flex h-14 items-center gap-3 border-l-4 bg-[#191937] bg-gradient-to-r to-transparent px-[18px] ${ARAM_MAYHEM_TIER_BANNER_CLASSES[tier]}`}
            >
              <span className={`block h-5 w-0.5 rounded-sm ${ARAM_MAYHEM_TIER_LABEL_BG[tier]}`} aria-hidden="true" />
              <h2 className="text-sm font-bold text-white">{tier}</h2>
            </div>
            <div className={`h-px ${ARAM_MAYHEM_TIER_LABEL_BG[tier]}`} aria-hidden="true" />
            <div
              className={`grid grid-cols-1 gap-x-[18px] gap-y-[18px] border-l-4 p-[18px] sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 ${ARAM_MAYHEM_TIER_BODY_BG[tier]} ${ARAM_MAYHEM_TIER_BANNER_CLASSES[tier]}`}
            >
              {augmentIds.map((augmentId) => {
                const entry = manifest[String(augmentId)];
                if (!entry) {
                  return (
                    <div key={augmentId} className="flex items-center gap-2">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] border border-dashed border-[#1c2030] bg-[#1a1d2e] font-mono text-[10px] text-[#64748b]">
                        ?
                      </div>
                      <span className="font-mono text-sm text-[#cddcea]">#{augmentId}</span>
                    </div>
                  );
                }
                return (
                  <div key={augmentId} className="flex items-center gap-2">
                    <img
                      src={`https://static.bigbrain.gg/cdragon-custom/${patch}/augments/${augmentId}.webp`}
                      alt={manifest[augmentId]}
                      loading="lazy"
                      className="h-9 w-9 shrink-0 rounded-[3px]"
                    />
                    <span className="font-[Barlow] text-sm font-semibold text-white">{manifest[augmentId]}</span>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Aggregated augment table — one global tier list across all
// rarities. Thin wrapper around AugmentTierRows; the wrapping exists
// for API symmetry with the champion table (both are exported as
// "...TierListTable") and for a clear extension point if the
// aggregated view eventually grows page-level chrome (filters,
// sort toggles, etc.).
// ─────────────────────────────────────────────────────────────────────

interface AramMayhemAugmentTierListTableProps {
  tierlist: Record<TierNameValue, number[]>;
  manifest: AugmentManifest;
  patch: string;
}

export function AramMayhemAugmentTierListTable(props: AramMayhemAugmentTierListTableProps) {
  const { tierlist, manifest, patch } = props || {};
  return <AugmentTierRows tierlist={tierlist} manifest={manifest} patch={patch} />;
}

// ─────────────────────────────────────────────────────────────────────
// By-rarity augment table — three sections (Prismatic / Gold /
// Silver), each containing an independent tier grouping. Iterates
// in the canonical best-to-worst order from RARITY_ORDER. A section
// is hidden entirely if its `tiers` payload is missing (defensive
// — the JSON always carries all three rarities, but a partial
// fetch or future schema variation shouldn't crash the page).
// ─────────────────────────────────────────────────────────────────────

interface AramMayhemAugmentByRarityTierListTableProps {
  rarities: Record<AugmentRarity, { tiers: Record<TierNameValue, number[]> }>;
  manifest: AugmentManifest;
  patch: string;
}

export function AramMayhemAugmentByRarityTierListTable(props: AramMayhemAugmentByRarityTierListTableProps) {
  const { rarities, manifest, patch } = props || {};

  return (
    <div className="flex flex-col gap-6">
      {RARITY_ORDER.map((rarity) => {
        const rarityTiers = rarities?.[rarity]?.tiers;
        if (!rarityTiers) return null;
        return (
          <section key={rarity} className="flex flex-col gap-2">
            <h3 className="text-lg font-bold text-[#e2e8f0]">{RARITY_DISPLAY[rarity]}</h3>
            <AugmentTierRows tierlist={rarityTiers} manifest={manifest} patch={patch} />
          </section>
        );
      })}
    </div>
  );
}

export const AramMayhemChampionTierListContainer = ({ bannerSlot, patchNotesCTA }: BannerProp) => {
  const queryParams = useQueryString();

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST, queryParams, true);
  const { patch } = validatedQueryParams;

  // Splice the validated patch back into the params dict so hooks
  // that read `patch` directly off `params` (manifest, etc.) see it
  // even when the URL omits the explicit `?patch=...` query —
  // validateQueryParams falls back to the latest patch when the
  // query key is missing, and we want every downstream fetch to
  // share that fallback rather than each hook having to know about
  // it independently. We DO still pass the same dict to the
  // tier-list hooks below (which run their own validateQueryParams
  // pass internally) — adding the resolved `patch` key here is a
  // no-op for them and a fix for the direct-params hooks.
  const params = { ...queryParams, patch };

  const { data, loading, error } = useAramMayhemChampionTierList({
    params,
  });
  const { tierlist, lastUpdated } = data || {};

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  let table = null;
  if (!window) {
    table = null;
  } else if (loading) {
    table = <TierListTablePlaceholder />;
  } else if (error) {
    table = <TierListTableError />;
  } else {
    table = <AramMayhemChampionTierListTable tierlist={tierlist || ({} as Record<TierNameValue, number[]>)} />;
  }

  return (
    <div>
      <TierListHeader
        className="mb-[24px]"
        totalMatches={0}
        lastUpdated={lastUpdated}
        loading={loading}
        includeMeta
        patchNotesCTA={patchNotesCTA}
      />
      <MediaQuery min="TABLET" max="DESKTOP_LARGE">
        <div>
          <TierListPageNav />
        </div>
      </MediaQuery>
      {bannerSlot ?? null}
      <TierListFilterManager className="my-[24px]" />
      <>{table}</>
    </div>
  );
};

export const AramMayhemAugmentTierListContainer = ({ bannerSlot, patchNotesCTA }: BannerProp) => {
  const queryParams = useQueryString();

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_AUGMENT_TIER_LIST, queryParams, true);
  const { patch } = validatedQueryParams;

  // Splice the validated patch back into the params dict — same
  // rationale as the champion container above. Specifically
  // important here because this container drives the manifest
  // fetch, and the manifest hook (post-refactor) reads `patch`
  // directly off params without running its own
  // validateQueryParams. Without this enrichment, URLs that omit
  // `?patch=...` would skip the manifest fetch entirely (via the
  // hook's effectiveSkip guard) and the augment tables would
  // render with placeholder content.
  const params = { ...queryParams, patch };

  // Augment data — aggregated. Independent fetch from the champion
  // one so a slow augment load doesn't block the champion table from
  // rendering (and vice versa). Variable names are prefixed to avoid
  // shadowing the champion destructures above.
  const { data: augmentData, loading: augmentLoading, error: augmentError } = useAramMayhemAugmentTierList({ params });
  const { tierlist: augmentTierlist, lastUpdated } = augmentData || {};

  // Augment manifest — id → {nameTRA, iconUrl, rarity, ...}. Both
  // augment tables look up against this dict to render real icons +
  // display names. Fetched once and shared (prop-drilled rather than
  // re-fetched per table) so the JSON fetcher's cache isn't strictly
  // necessary for correctness. The data IS just an id→entry dict —
  // no `lastUpdated` envelope — so the cleaner returns the dict
  // directly with an empty-object fallback during loading.
  const { data: manifestData, loading: manifestLoading, error: manifestError } = useAramMayhemAugmentManifest({ params });
  const manifest = manifestData || ({} as AugmentManifest);

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  // Augment table — same loading/error/render pattern as the champion
  // one but driven by the separate augment fetch. SSR guard mirrors
  // the existing pattern (skip render until `window` is defined).
  // The manifest fetch is folded into the augment table's loading +
  // error gating because the table can't render meaningful content
  // without the manifest — defer the table until BOTH are ready,
  // rather than showing one render with placeholder fallback and
  // then a second render once the manifest arrives.
  let augmentTable = null;
  if (!window) {
    augmentTable = null;
  } else if (augmentLoading || manifestLoading) {
    augmentTable = <TierListTablePlaceholder />;
  } else if (augmentError || manifestError) {
    augmentTable = <TierListTableError />;
  } else {
    augmentTable = (
      <AramMayhemAugmentTierListTable
        tierlist={augmentTierlist || ({} as Record<TierNameValue, number[]>)}
        manifest={manifest}
        patch={patch}
      />
    );
  }

  return (
    <div>
      <TierListHeader
        className="mb-[24px]"
        totalMatches={0}
        lastUpdated={lastUpdated}
        loading={augmentLoading || manifestLoading}
        includeMeta
        patchNotesCTA={patchNotesCTA}
      />
      <MediaQuery min="TABLET" max="DESKTOP_LARGE">
        <div>
          <TierListPageNav />
        </div>
      </MediaQuery>
      {bannerSlot ?? null}
      <TierListFilterManager className="my-[24px]" />
      <>{augmentTable}</>
    </div>
  );
};

//--------------------------------------------------------------------------------

export const AramMayhemAugmentTierListByRarityContainer = ({ bannerSlot, patchNotesCTA }: BannerProp) => {
  const queryParams = useQueryString();

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_AUGMENT_TIER_LIST, queryParams, true);
  const { patch } = validatedQueryParams;

  // Splice the validated patch back into the params dict — same
  // rationale as the aggregated augment container above. The
  // by-rarity hook and the manifest hook both read `patch` directly
  // from params (they bypass their own validateQueryParams call), so
  // this enrichment is what keeps them both driven off the validated
  // value when the URL omits `?patch=...`.
  const params = { ...queryParams, patch };

  // By-rarity augment data. Independent fetch from the aggregated
  // augment endpoint above — different URL, different JSON file,
  // so a page swap between the two variants triggers a fresh
  // fetch of the underlying data (but NOT of the manifest — see
  // below).
  const { data: augmentData, loading: augmentLoading, error: augmentError } = useAramMayhemAugmentByRarityTierList({ params });
  const { rarities: augmentRarities, lastUpdated } = augmentData || {};

  // Augment manifest — same URL and same cache slot the aggregated
  // container hits, so swapping between the two variants at the
  // same route reuses this fetch across renders. No additional
  // network traffic attributable to the container swap.
  const { data: manifestData, loading: manifestLoading, error: manifestError } = useAramMayhemAugmentManifest({ params });
  const manifest = manifestData || ({} as AugmentManifest);

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  // Same loading/error/render pattern as the aggregated container.
  // Gate on BOTH the by-rarity data AND the manifest so the table
  // renders once with real content — no placeholder-to-real
  // flash mid-load, which is particularly jarring with the
  // three-section by-rarity layout since sections would pop in
  // one at a time as manifest lookups resolve.
  let augmentTable = null;
  if (!window) {
    augmentTable = null;
  } else if (augmentLoading || manifestLoading) {
    augmentTable = <TierListTablePlaceholder />;
  } else if (augmentError || manifestError) {
    augmentTable = <TierListTableError />;
  } else {
    augmentTable = (
      <AramMayhemAugmentByRarityTierListTable
        rarities={augmentRarities || ({} as Record<AugmentRarity, { tiers: Record<TierNameValue, number[]> }>)}
        manifest={manifest}
        patch={patch}
      />
    );
  }

  return (
    <div>
      <TierListHeader
        className="mb-[24px]"
        totalMatches={0}
        lastUpdated={lastUpdated}
        loading={augmentLoading || manifestLoading}
        includeMeta
        patchNotesCTA={patchNotesCTA}
      />
      <MediaQuery min="TABLET" max="DESKTOP_LARGE">
        <div>
          <TierListPageNav />
        </div>
      </MediaQuery>
      {bannerSlot ?? null}
      <TierListFilterManager className="my-[24px]" />
      <>{augmentTable}</>
    </div>
  );
};

//--------------------------------------------------------------------------------

// Per-champion augment tier list, scoped to ONE champion's
// observations of augments. Renders inside the champion-build page
// (not the tier-list page), so the data flow differs from the
// route-bound containers above in two ways: the caller passes
// `championKey` (numeric riot champion id — 62 for MonkeyKing, 145
// for Kaisa, ...) and `params` explicitly rather than letting the
// container scrape them from useQueryString / validateQueryParams;
// and the page-chrome (TierListHeader, MediaQuery + TierListPageNav,
// TierListFilterManager, banner slot, patch-notes CTA) is omitted
// because the build page provides its own surrounding layout.
//
// The same `AramMayhemAugmentTierListTable` used by the global
// augment container above is reused here — the JSON shape is
// identical (id → tier mapping), so there's no rendering
// difference, just a different fetch source.
//
// Manifest fetching: we reuse `useAramMayhemAugmentManifest` from
// above, which (post-refactor) also reads `patch` directly off
// caller-supplied params rather than running its own
// validateQueryParams. So the same `params` dict that drives the
// per-champion tier-list fetch above drives the manifest fetch
// here — no second resolution path, no risk of the two hooks
// disagreeing on which patch they're targeting. Both hooks'
// effectiveSkip guards short-circuit cleanly if `patch` ever
// goes missing.
interface AramMayhemPerChampionAugmentTierListContainerProps {
  championKey: number;
  params: Record<string, any>;
}

export const AramMayhemPerChampionAugmentTierListContainer = ({
  championKey,
  params,
}: AramMayhemPerChampionAugmentTierListContainerProps) => {
  // Per-champion tier list — bypasses validateQueryParams, takes
  // patch straight off the caller-supplied params dict (see the
  // hook's own comment for the rationale).
  const {
    data: augmentData,
    loading: augmentLoading,
    error: augmentError,
  } = useAramMayhemPerChampionAugmentTierList({ championKey, params });
  const { tierlist: augmentTierlist } = augmentData || {};

  // Augment manifest — shared lookup table for icons + display
  // names. The augment table can render placeholders without the
  // manifest, but the result is unreadable (just "#1234" labels),
  // so we gate the table on BOTH fetches being ready, same as the
  // global augment container.
  const { data: manifestData, loading: manifestLoading, error: manifestError } = useAramMayhemAugmentManifest({ params });
  const manifest = manifestData || ({} as AugmentManifest);

  const { patch } = params;

  // Loading / error / render gating. typeof window check guards
  // SSR — JSONFetcher works in both environments but the table's
  // <img> tags fire lazy loads that only make sense client-side.
  let table = null;
  if (typeof window === "undefined") {
    table = null;
  } else if (augmentLoading || manifestLoading) {
    table = <TierListTablePlaceholder />;
  } else if (augmentError || manifestError) {
    table = <TierListTableError />;
  } else {
    table = (
      <AramMayhemAugmentTierListTable
        tierlist={augmentTierlist || ({} as Record<TierNameValue, number[]>)}
        manifest={manifest}
        patch={patch}
      />
    );
  }

  return <div>{table}</div>;
};

//--------------------------------------------------------------------------------

const AramMayhemBuild = () => {
  const { championId, page } = useChampionProfileContext();

  // championId is the numeric champion KEY (62 for MonkeyKing, 145
  // for Kaisa, ...) — the same identifier data_tierlists-html.py
  // uses as the filename stem for the per-champion augment JSONs
  // (after resolving SKIN → key via the mapping file). We pass it
  // through as-is; no name conversion needed anymore.

  const queryParams = useQueryString();
  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(page, queryParams, true);

  return <AramMayhemPerChampionAugmentTierListContainer championKey={championId} params={validatedQueryParams} />;
};

export default AramMayhemBuild;

//--------------------------------------------------------------------------------
